import React, { useState, useMemo } from 'react';

/**
 * RevenueCalculator — Interactive revenue breakdown calculator
 * Shows transparent fee breakdown (all from gross)
 *
 * Props (read from data-* attributes by the entry file):
 * - platformFeeInvited: number (default: 0.00)
 * - platformFeePlatform: number (default: 0.15)
 * - defaultOwnerCommissionInvited: number (default: 0.10)
 * - defaultOwnerCommissionPlatform: number (default: 0.05)
 * - serviceFeeRate: number (default: 0.10)
 * - variant: 'full' | 'compact'
 */
export default function RevenueCalculator({
  platformFeeInvited = 0.00,
  platformFeePlatform = 0.15,
  defaultOwnerCommissionInvited = 0.10,
  defaultOwnerCommissionPlatform = 0.05,
  serviceFeeRate = 0.10,
  variant = 'full',
  feeLabels = {}
}) {
  const [lessonPrice, setLessonPrice] = useState(1000);
  const [studentSource, setStudentSource] = useState('invited');
  const [commissionInvited, setCommissionInvited] = useState(defaultOwnerCommissionInvited * 100);
  const [commissionPlatform, setCommissionPlatform] = useState(defaultOwnerCommissionPlatform * 100);
  const [referralBonus, setReferralBonus] = useState(0);

  const ownerCommission = studentSource === 'invited' ? commissionInvited : commissionPlatform;
  const setOwnerCommission = studentSource === 'invited' ? setCommissionInvited : setCommissionPlatform;
  const maxCommission = studentSource === 'invited' ? 50 : 10;

  // Referral bonus cannot exceed owner commission
  const effectiveReferralBonus = Math.min(referralBonus, ownerCommission);

  const handleSourceChange = (source) => {
    setStudentSource(source);
  };

  const breakdown = useMemo(() => {
    const price = Math.max(0, lessonPrice || 0);
    const platformRate = studentSource === 'invited' ? platformFeeInvited : platformFeePlatform;
    const commissionRate = Math.min(maxCommission, Math.max(0, ownerCommission)) / 100;
    const referralRate = Math.min(effectiveReferralBonus, ownerCommission) / 100;

    // All fees calculated from gross
    const platformFee = Math.round(price * platformRate);
    const totalLeaderCommission = Math.round(price * commissionRate);
    const referralAmount = Math.round(price * referralRate);
    const leaderEarnings = totalLeaderCommission - referralAmount;
    const serviceFee = Math.round(price * serviceFeeRate);
    const teacherEarnings = price - platformFee - totalLeaderCommission - serviceFee;

    // Percentages for the bar (relative to total price)
    const platformPct = price > 0 ? (platformFee / price) * 100 : 0;
    const leaderPct = price > 0 ? (leaderEarnings / price) * 100 : 0;
    const referralPct = price > 0 ? (referralAmount / price) * 100 : 0;
    const serviceFeePct = price > 0 ? (serviceFee / price) * 100 : 0;
    const teacherPct = price > 0 ? (teacherEarnings / price) * 100 : 0;

    return {
      price,
      platformFee,
      totalLeaderCommission,
      leaderEarnings,
      referralAmount,
      serviceFee,
      teacherEarnings,
      platformRate,
      platformPct,
      leaderPct,
      referralPct,
      serviceFeePct,
      teacherPct
    };
  }, [lessonPrice, studentSource, commissionInvited, commissionPlatform, maxCommission, effectiveReferralBonus, ownerCommission, platformFeeInvited, platformFeePlatform, serviceFeeRate]);

  const formatNTD = (amount) => `NT$${amount.toLocaleString()}`;

  if (variant === 'compact') {
    return <CompactCalculator breakdown={breakdown} formatNTD={formatNTD} />;
  }

  return (
    <div className="space-y-6">
      {/* Input Controls */}
      <div className="space-y-5">
        {/* Lesson Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {feeLabels.lesson_price || 'Lesson Price'}
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">NT$</span>
            <input
              type="number"
              min="0"
              step="100"
              value={lessonPrice}
              onChange={(e) => setLessonPrice(parseInt(e.target.value) || 0)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]/20 outline-none transition-all text-lg font-medium"
            />
          </div>
        </div>

        {/* Student Source Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {feeLabels.student_source || 'Student Source'}
          </label>
          <div className="inline-flex rounded-full bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => handleSourceChange('invited')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                studentSource === 'invited'
                  ? 'bg-white text-[#3366FF] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {feeLabels.invited_student || 'Invited Student'} ({Math.round(platformFeeInvited * 100)}%)
            </button>
            <button
              type="button"
              onClick={() => handleSourceChange('platform')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                studentSource === 'platform'
                  ? 'bg-white text-[#3366FF] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {feeLabels.platform_student || 'Platform Student'} ({Math.round(platformFeePlatform * 100)}%)
            </button>
          </div>
        </div>

        {/* Owner Commission Slider */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {feeLabels.owner_commission || 'Owner Management Fee'}
          </label>
          <div className="relative pt-6">
            <div
              className="absolute -top-0 bg-[#3366FF] text-white rounded-md px-2 py-0.5 text-xs font-bold pointer-events-none"
              style={{
                left: `calc(${maxCommission > 0 ? (ownerCommission / maxCommission) * 100 : 0}% + ${8 - (maxCommission > 0 ? (ownerCommission / maxCommission) : 0) * 16}px)`,
                transform: 'translateX(-50%)'
              }}
            >
              {Math.round(ownerCommission)}%
            </div>
            <input
              type="range"
              min="0"
              max={maxCommission}
              step="1"
              value={ownerCommission}
              onChange={(e) => setOwnerCommission(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#3366FF]"
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>0%</span>
            <span>{maxCommission}%</span>
          </div>
        </div>

        {/* Referral Bonus Slider */}
        {ownerCommission > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {feeLabels.referral_bonus || 'Referral Bonus'}
            </label>
            <p className="text-xs text-gray-400 mb-3">{feeLabels.referral_bonus_hint || 'Allocated from management fee to reward teachers who bring in students'}</p>
            <div className="relative pt-6">
              <div
                className="absolute -top-0 bg-teal-500 text-white rounded-md px-2 py-0.5 text-xs font-bold pointer-events-none"
                style={{
                  left: `calc(${ownerCommission > 0 ? (effectiveReferralBonus / ownerCommission) * 100 : 0}% + ${8 - (ownerCommission > 0 ? (effectiveReferralBonus / ownerCommission) : 0) * 16}px)`,
                  transform: 'translateX(-50%)'
                }}
              >
                {Math.round(effectiveReferralBonus)}%
              </div>
              <input
                type="range"
                min="0"
                max={Math.round(ownerCommission)}
                step="1"
                value={effectiveReferralBonus}
                onChange={(e) => setReferralBonus(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
              />
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>0%</span>
              <span>{Math.round(ownerCommission)}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Visual Bar */}
      <BreakdownBar breakdown={breakdown} feeLabels={feeLabels} />

      {/* Breakdown Details */}
      <BreakdownDetails
        breakdown={breakdown}
        formatNTD={formatNTD}
        ownerCommission={ownerCommission}
        referralBonus={effectiveReferralBonus}
        feeLabels={feeLabels}
      />

      {/* Leader Earnings Estimate */}
      {breakdown.totalLeaderCommission > 0 && (
        <LeaderEarningsEstimate
          leaderEarningsPerLesson={breakdown.leaderEarnings}
          referralPerLesson={breakdown.referralAmount}
          formatNTD={formatNTD}
          feeLabels={feeLabels}
        />
      )}
    </div>
  );
}

function BreakdownBar({ breakdown, feeLabels = {} }) {
  const { platformPct, leaderPct, referralPct, serviceFeePct, teacherPct } = breakdown;

  const segments = [
    { pct: platformPct, color: 'bg-gray-300', textColor: 'text-gray-600', label: feeLabels.platform_fee || 'Platform Fee' },
    { pct: leaderPct, color: 'bg-[#6699FF]', textColor: 'text-white', label: feeLabels.management_fee || 'Management Fee' },
    { pct: referralPct, color: 'bg-teal-400', textColor: 'text-white', label: feeLabels.referral_bonus || 'Referral Bonus' },
    { pct: serviceFeePct, color: 'bg-[#FBBF24]', textColor: 'text-white', label: feeLabels.service_fee || 'Service Fee' },
    { pct: teacherPct, color: 'bg-[#3366FF]', textColor: 'text-white', label: feeLabels.teacher_earnings || 'Teacher Earnings' },
  ];

  // Only show segments that have a value
  const visibleSegments = segments.filter(s => s.pct > 0);

  return (
    <div className="space-y-2">
      <div className="flex rounded-xl overflow-hidden h-10" role="img" aria-label="Revenue distribution breakdown">
        {visibleSegments.map(({ pct, color, textColor, label }) => (
          <div
            key={label}
            className={`${color} flex items-center justify-center text-xs font-medium ${textColor} transition-all duration-300`}
            style={{ width: `${pct}%` }}
            title={`${label} ${pct.toFixed(1)}%`}
          >
            {pct >= 6 && `${pct.toFixed(0)}%`}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
        {visibleSegments.map(({ color, label }) => (
          <span key={label} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-sm ${color}`}></span>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function BreakdownDetails({ breakdown, formatNTD, ownerCommission, referralBonus = 0, feeLabels = {} }) {
  const { price, platformFee, leaderEarnings, referralAmount, serviceFee, teacherEarnings, platformRate } = breakdown;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <span className="text-sm text-gray-600">{feeLabels.student_payment || 'Student Payment'}</span>
        <span className="text-sm font-medium text-gray-900">{formatNTD(price)}</span>
      </div>
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <span className="text-sm text-gray-600">
          {feeLabels.platform_fee || 'Platform Fee'} ({Math.round(platformRate * 100)}%)
        </span>
        <span className="text-sm font-medium text-gray-500">-{formatNTD(platformFee)}</span>
      </div>
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <span className="text-sm text-gray-600">
          {feeLabels.owner_commission || 'Owner Commission'} ({Math.round(ownerCommission)}%{referralBonus > 0 ? ` - ${Math.round(referralBonus)}% ${feeLabels.referral || 'referral'}` : ''})
        </span>
        <span className="text-sm font-medium text-[#6699FF]">-{formatNTD(leaderEarnings)}</span>
      </div>
      {referralAmount > 0 && (
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-sm text-gray-600">
            {feeLabels.referral_bonus || 'Referral Bonus'} ({Math.round(referralBonus)}%)
          </span>
          <span className="text-sm font-medium text-teal-600">-{formatNTD(referralAmount)}</span>
        </div>
      )}
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <span className="text-sm text-gray-600">
          {feeLabels.service_fee || 'Service Fee'} (10%)
        </span>
        <span className="text-sm font-medium text-[#D97706]">-{formatNTD(serviceFee)}</span>
      </div>
      <div className="flex items-center justify-between py-3 bg-[#E6F0FF] rounded-xl px-4 -mx-1">
        <span className="text-sm font-bold text-gray-900">{feeLabels.teacher_earnings || 'Teacher Take-Home'}</span>
        <span className="text-lg font-bold text-[#3366FF]">{formatNTD(teacherEarnings)}</span>
      </div>
    </div>
  );
}

function LeaderEarningsEstimate({ leaderEarningsPerLesson, referralPerLesson = 0, formatNTD, feeLabels = {} }) {
  const [teacherCount, setTeacherCount] = useState(5);
  const [lessonsPerMonth, setLessonsPerMonth] = useState(20);

  const safeTeacherCount = parseInt(teacherCount) || 0;
  const safeLessonsPerMonth = parseInt(lessonsPerMonth) || 0;
  const monthlyEarnings = safeTeacherCount * safeLessonsPerMonth * leaderEarningsPerLesson;
  const monthlyReferral = safeTeacherCount * safeLessonsPerMonth * referralPerLesson;

  return (
    <div className="rounded-2xl border-2 border-purple-200 bg-purple-50/50 p-5 space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-lg">👑</span>
        <h4 className="text-sm font-bold text-purple-800">{feeLabels.owner_estimate_title || 'Owner Earnings Estimate'}</h4>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-purple-700">{feeLabels.net_fee_per_lesson || 'Net management fee per lesson'}</span>
        <span className="text-sm font-bold text-purple-900">{formatNTD(leaderEarningsPerLesson)}</span>
      </div>
      {referralPerLesson > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-teal-700">{feeLabels.referral_paid_per_lesson || 'Referral bonus paid per lesson'}</span>
          <span className="text-sm font-bold text-teal-600">-{formatNTD(referralPerLesson)}</span>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-purple-700 whitespace-nowrap">{feeLabels.teachers_managed || 'Teachers managed'}</label>
          <input
            type="number"
            min="1"
            max="50"
            value={teacherCount}
            onChange={(e) => setTeacherCount(e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0))}
            className="w-16 px-2 py-1.5 rounded-lg border border-purple-200 text-center text-sm font-medium focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none"
          />
        </div>
        <span className="text-purple-400 font-medium">×</span>
        <div className="flex items-center gap-2">
          <label className="text-sm text-purple-700 whitespace-nowrap">{feeLabels.lessons_per_month || 'Lessons per month'}</label>
          <input
            type="number"
            min="0"
            max="200"
            value={lessonsPerMonth}
            onChange={(e) => setLessonsPerMonth(e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0))}
            className="w-16 px-2 py-1.5 rounded-lg border border-purple-200 text-center text-sm font-medium focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none"
          />
        </div>
      </div>

      <div className="bg-purple-100 rounded-xl p-4">
        <div className="text-xs text-purple-600 mb-1">{feeLabels.estimated_monthly || 'Estimated monthly net management earnings'}</div>
        <div className="text-xl font-bold text-purple-900">{formatNTD(monthlyEarnings)}</div>
        <div className="text-xs text-purple-500 mt-1">
          = {safeTeacherCount} {feeLabels.teachers_unit || 'teachers'} × {safeLessonsPerMonth} {feeLabels.lessons_unit || 'lessons'} × {formatNTD(leaderEarningsPerLesson)}
        </div>
        {monthlyReferral > 0 && (
          <div className="text-xs text-teal-600 mt-1">
            {feeLabels.referral_paid_total || 'Referral bonuses paid'}: -{formatNTD(monthlyReferral)}
          </div>
        )}
      </div>
    </div>
  );
}

function CompactCalculator({ breakdown, formatNTD }) {
  return (
    <div className="bg-[#E6F0FF]/50 rounded-2xl p-6">
      <p className="text-sm text-gray-600 mb-4">
        Example with {formatNTD(breakdown.price)} lesson (invited student):
      </p>

      {/* Mini bar */}
      <div className="flex rounded-lg overflow-hidden h-6 mb-4">
        {breakdown.platformPct > 0 && (
          <div
            className="bg-gray-300"
            style={{ width: `${breakdown.platformPct}%` }}
          />
        )}
        {breakdown.leaderPct > 0 && (
          <div
            className="bg-[#6699FF]"
            style={{ width: `${breakdown.leaderPct}%` }}
          />
        )}
        {breakdown.referralPct > 0 && (
          <div
            className="bg-teal-400"
            style={{ width: `${breakdown.referralPct}%` }}
          />
        )}
        {breakdown.serviceFeePct > 0 && (
          <div
            className="bg-[#FBBF24]"
            style={{ width: `${breakdown.serviceFeePct}%` }}
          />
        )}
        <div
          className="bg-[#3366FF]"
          style={{ width: `${breakdown.teacherPct}%` }}
        />
      </div>

      <div className="grid grid-cols-3 gap-2 text-center mb-4">
        <div>
          <div className="text-xs text-gray-500">Platform+Service Fee</div>
          <div className="text-sm font-bold text-gray-600">
            {formatNTD(breakdown.platformFee + breakdown.serviceFee)}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Owner</div>
          <div className="text-sm font-bold text-[#6699FF]">{formatNTD(breakdown.leaderEarnings)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Teacher</div>
          <div className="text-sm font-bold text-[#3366FF]">{formatNTD(breakdown.teacherEarnings)}</div>
        </div>
      </div>

      <a
        href="/faq#revenue-calculator"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3366FF] hover:text-[#2952CC] transition-colors"
      >
        Try the calculator
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </a>
    </div>
  );
}
